<template>
  <el-dialog
    class="upload-confirm-dialog"
    :model-value="visible"
    :title="$t('categoryForm.confirmDialog.title')"
    width="min(560px, 92vw)"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    :show-close="true"
    @close="handleCancel"
  >
    <div class="confirm-dialog-content">
      <div class="summary-row">
        <span class="label">{{ $t('categoryForm.confirmDialog.fileName') }}</span>
        <span class="value">{{ fileName }}</span>
      </div>
      <div class="summary-row">
        <span class="label">{{ $t('categoryForm.confirmDialog.pageCount') }}</span>
        <span class="value">{{ pageCountDisplay }}</span>
      </div>

      <div class="language-row">
        <div class="language-group">
          <label class="language-label">{{ $t('categoryForm.confirmDialog.sourceLanguage') }}</label>
          <el-select
            v-model="localSourceLanguage"
            size="large"
            class="language-select"
          >
            <el-option
              v-for="lang in sourceLanguageOptions"
              :key="lang.code"
              :label="lang.name"
              :value="lang.code"
            />
          </el-select>
        </div>

        <div class="language-group">
          <label class="language-label">{{ $t('categoryForm.confirmDialog.targetLanguage') }}</label>
          <el-select
            v-model="localTargetLanguage"
            size="large"
            class="language-select"
          >
            <el-option
              v-for="lang in targetLanguageOptions"
              :key="lang.code"
              :label="lang.name"
              :value="lang.code"
            />
          </el-select>
        </div>
      </div>

      <p class="dialog-hint">{{ $t('categoryForm.confirmDialog.hint') }}</p>
    </div>

    <template #footer>
      <div class="dialog-actions">
        <el-button @click="handleCancel">
          {{ $t('categoryForm.confirmDialog.cancel') }}
        </el-button>
        <el-button
          type="primary"
          :disabled="!localTargetLanguage || !localSourceLanguage"
          @click="handleConfirm"
        >
          {{ $t('categoryForm.confirmDialog.confirm') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { LanguageInfo } from '~/types/translation'

interface Props {
  visible: boolean
  fileName: string
  pages?: number
  sourceLanguage: string
  targetLanguage: string
  sourceLanguageOptions: LanguageInfo[]
  targetLanguageOptions: LanguageInfo[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  cancel: []
  confirm: [{ sourceLanguage: string; targetLanguage: string }]
}>()

const localSourceLanguage = ref('')
const localTargetLanguage = ref('')

const toPageDisplay = (value?: number) => {
  if (value === undefined || value === null) {
    return '--'
  }
  return `${value}`
}

const pageCountDisplay = computed(() => toPageDisplay(props.pages))

watch(
  () => [props.visible, props.sourceLanguage, props.targetLanguage],
  () => {
    localSourceLanguage.value = props.sourceLanguage || ''
    localTargetLanguage.value = props.targetLanguage || ''
  },
  { immediate: true }
)

const handleCancel = () => {
  emit('cancel')
}

const handleConfirm = () => {
  if (!localSourceLanguage.value || !localTargetLanguage.value) {
    return
  }
  emit('confirm', {
    sourceLanguage: localSourceLanguage.value,
    targetLanguage: localTargetLanguage.value
  })
}
</script>

<style scoped lang="scss">
.upload-confirm-dialog :deep(.el-dialog) {
  width: min(560px, 92vw) !important;
  max-width: 560px;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-lg;
  overflow: hidden;
}

.upload-confirm-dialog :deep(.el-dialog__header) {
  margin-right: 0;
  padding: $spacing-lg $spacing-lg $spacing-sm;
  border-bottom: 1px solid $gray-200;
}

.upload-confirm-dialog :deep(.el-dialog__title) {
  font-weight: 700;
  color: $gray-900;
}

.upload-confirm-dialog :deep(.el-dialog__body) {
  padding: $spacing-md $spacing-lg;
}

.upload-confirm-dialog :deep(.el-dialog__footer) {
  padding: $spacing-sm $spacing-lg $spacing-lg;
  border-top: 1px solid $gray-100;
}

.confirm-dialog-content {
  display: flex;
  flex-direction: column;
  gap: $spacing-sm;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: $spacing-sm;

  .label {
    color: $gray-600;
  }

  .value {
    color: $gray-900;
    font-weight: 600;
    word-break: break-all;
  }
}

.language-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $spacing-md;

  .language-group {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
  }

  .language-label {
    font-size: 0.85rem;
    color: $gray-600;
  }

  .language-select {
    width: 100%;
  }
}

.dialog-hint {
  margin: 0;
  color: $gray-500;
  font-size: 0.85rem;
  padding-top: $spacing-xs;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: $spacing-sm;
}

@media (max-width: 768px) {
  .upload-confirm-dialog :deep(.el-dialog) {
    width: 94vw !important;
  }

  .language-row {
    grid-template-columns: 1fr;
  }
}
</style>
